import { Box, Typography } from "@mui/material";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import CloudUploadSVG from "../../../../../assets/TailoredGeneration/icons/cloud_upload.svg";
import UploadSVG from "../../../../../assets/TailoredGeneration/icons/tg_upload_icon.svg";
import FileUploader from "../../../../../components/common/FileUploader/FileUploader";
import ImageUploader from "../../../../../components/common/ImageUploader/ImageUploader";
import RouterConstants from "../../../../../constants/RouterConstants";
import { useAppStore } from "../../../../../hooks/useStores";
import { TgDataset, TgDatasetImage } from "../../../Datasets/store/tg-dataset-store.tsx";
import { canTgProjectGeneratePrefix, isLegacyTgRoute } from "../../../utils/tg-utils.ts";
import TgDatasetTrainingGuide from "../components/TgDatasetTrainingGuide/TgDatasetTrainingGuide.tsx";
import styles from "./NewTgUploadMethod.module.scss";

type Props = {
	handleUploadImagesNext?: () => void;
	handleUseAdvancedNext?: () => void;
};

const NewTgUploadMethod = ({ handleUploadImagesNext, handleUseAdvancedNext }: Props) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.newUploadMethod" });
	const { tgStore } = useAppStore();
	const { tgProjectStore, tgModelStore, tgDatasetStore } = tgStore;
	const uploadingDatasetImages =
		tgDatasetStore.loadingUpdateDataset ||
		tgDatasetStore.loadingDuplicateDataset ||
		tgDatasetStore.loadingUploadImages ||
		tgDatasetStore.loadingRegenerate ||
		tgDatasetStore.loadingCreateDataset ||
		tgDatasetStore.loadingGeneratePrefix;

	const navigate = useNavigate();
	const { projectId } = useParams();

	const createAndUploadDataset = async (e: ChangeEvent<HTMLInputElement>) => {
		const files: File[] | null = Array.from(e.target.files || []);
		e.target.value = "";
		if (!files.length) return;

		try {
			const newDataset: TgDataset = await tgDatasetStore.createDataset({
				...tgDatasetStore.datasetForm,
				project_id: Number(projectId),
				training_version: "fibo",
				name: "temp",
			});

			tgDatasetStore.datasetForm = { ...newDataset, images: [] };
			tgModelStore.modelForm.dataset_id = newDataset.id;

			handleUploadImagesNext && handleUploadImagesNext();

			await tgDatasetStore.uploadImages(newDataset.id, files);
			const supportedImages: TgDatasetImage[] = tgDatasetStore.datasetForm.images.filter((image) => !image.error);

			if (canTgProjectGeneratePrefix(tgProjectStore.projectToView.ip_type)) {
				const supportedImagesUrls: string[] = supportedImages.map((image) => image.image_url);
				const generatedPrefix = await tgDatasetStore.imagesSampleGeneratePrefix(tgProjectStore.projectToView, {
					imagesURLs: supportedImagesUrls,
				});

				if (generatedPrefix) {
					newDataset.caption_prefix = generatedPrefix.prefix;
				}
			}

			newDataset.images = supportedImages;
			await tgDatasetStore.updateDataset(newDataset.id, newDataset);

			tgDatasetStore.datasetForm = { ...newDataset, images: tgDatasetStore.datasetForm.images };

			if (handleUploadImagesNext) {
				handleUploadImagesNext();
			} else {
				const editDatasetState = { datasetToEdit: toJS(tgDatasetStore.datasetForm) };
				const base = isLegacyTgRoute()
					? RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath
					: RouterConstants.NEW_TG.PROJECTS.fullPath;
				navigate(`${base}/${projectId}/${RouterConstants.NEW_TG.DATASETS.path}/${newDataset.id}/edit`, {
					state: editDatasetState,
				});
			}
		} catch (error) {
			console.error("Error creating or uploading dataset:", error);
			tgDatasetStore.loadingCreateDataset = false;
			tgDatasetStore.loadingGeneratePrefix = false;
			tgDatasetStore.loadingUploadImages = false;
		}
	};

	return (
		<Box className={styles.splitContainer}>
			<Box className={styles.leftPane}>
				<Box className={styles.container}>
					<Box className={styles.topSpacer} />
					<Typography className={styles.title}>{t("title")}</Typography>
					<Typography className={styles.description}>{t("description")}</Typography>

					<Box className={styles.uploaderWrapper}>
						<ImageUploader
							className={styles.uploader}
							emptyStateClassName={styles.emptyStateOverride}
							droppable
							showPreview={false}
							hideSupportElement
							icon={<img src={CloudUploadSVG} className={styles.cloudIcon} />}
							description={
								<>
									<Typography className={styles.dndDescription}>{t("dragText")}</Typography>
									<Typography className={styles.dndSubText}>{t("uploadLimitText")}</Typography>
								</>
							}
							loading={uploadingDatasetImages}
							inputProps={{ accept: "image/png, image/jpg, image/jpeg, image/webp", multiple: true }}
							onUpload={createAndUploadDataset}
						>
							<Box className={styles.orDivider}>{t("or")}</Box>
							<FileUploader
								buttonText={t("browseButton")}
								buttonType="primaryMedium"
								onUpload={createAndUploadDataset}
								maxFilesLimit={200}
								inputProps={{ accept: "image/png, image/jpg, image/jpeg, image/webp", multiple: true }}
								buttonProps={{ startIcon: <img src={UploadSVG} className={styles.browseIcon} /> }}
								loading={uploadingDatasetImages}
								disabled={uploadingDatasetImages}
								buttonClassName={styles.browseBtn}
							/>
						</ImageUploader>
					</Box>

					<Typography className={styles.largeDatasetText}>{t("largeDatasetText")}</Typography>
					<Typography className={styles.advancedLink} onClick={handleUseAdvancedNext}>
						{t("switchToAdvanced")}
					</Typography>
				</Box>
			</Box>
			<Box className={styles.rightPane}>
				<TgDatasetTrainingGuide />
			</Box>
		</Box>
	);
};

export default observer(NewTgUploadMethod);
